import {
  AdminCronParam,
  AdminCronParamValues,
  AdminCronRunResult,
  AdminCronVariant,
} from "../models/admin-cron.models";
import { ADMIN_CRON_CARGILL_EMAIL_PATTERN } from "./admin-cron.constants";

interface ResultMetric {
  label: string;
  value: string | number;
  tone: "success" | "warning" | "danger" | "neutral";
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const countPayloadItems = (value: unknown): number => {
  if (Array.isArray(value)) {
    return value.length;
  }

  if (!isRecord(value)) {
    return value ? 1 : 0;
  }

  const nestedCounts = Object.values(value).reduce<number[]>((counts, entry) => {
    if (Array.isArray(entry)) {
      counts.push(entry.length);
    }

    return counts;
  }, []);

  if (nestedCounts.length > 0) {
    return nestedCounts.reduce((sum, count) => sum + count, 0);
  }

  return Object.keys(value).length;
};

const getNestedValue = (value: unknown, key: string) => (isRecord(value) ? value[key] : undefined);

export const getCronResultSummary = (result: AdminCronRunResult) => {
  if (result.status === "error") {
    return result.errorMessage ?? "Cron job request failed.";
  }

  const summary = getNestedValue(result.data, "summary");
  if (typeof summary === "string" && summary.trim()) {
    return summary;
  }

  if (Array.isArray(result.data)) {
    return `Returned ${result.data.length} records.`;
  }

  if (typeof result.data === "string") {
    return result.data;
  }

  return "Run completed. Review the result details below.";
};

export const getCronResultMetrics = (result: AdminCronRunResult): ResultMetric[] => {
  const details = getNestedValue(result.data, "details");
  const errors = getNestedValue(result.data, "errors");
  const run = getNestedValue(result.data, "run");
  const dataCount = countPayloadItems(result.data);
  const errorCount = countPayloadItems(errors);

  return [
    {
      label: "Status",
      value: result.status === "success" ? "OK" : "ERROR",
      tone: result.status === "success" ? "success" : "danger",
    },
    {
      label: "Mode",
      value: result.risk === "prod" ? "PROD" : result.risk === "dryRun" ? "DRY RUN" : "VERIFY",
      tone: result.risk === "prod" ? "danger" : result.risk === "dryRun" ? "success" : "warning",
    },
    {
      label: "Run flag",
      value: typeof run === "boolean" ? (run ? "TRUE" : "FALSE") : "N/A",
      tone: run === true ? "danger" : run === false ? "success" : "neutral",
    },
    {
      label: "Items",
      value: details ? countPayloadItems(details) : dataCount,
      tone: "neutral",
    },
    {
      label: "Errors",
      value: errorCount,
      tone: errorCount > 0 || result.status === "error" ? "danger" : "success",
    },
    {
      label: "Duration",
      value: `${Math.round(result.durationMs / 100) / 10}s`,
      tone: "neutral",
    },
  ];
};

export const getObjectEntries = (value: unknown) => (isRecord(value) ? Object.entries(value) : []);

export const formatCronValue = (value: unknown): string => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return JSON.stringify(value, null, 2);
};

const hasParamValue = (value: string | boolean | undefined): boolean =>
  typeof value === "boolean" ? value : Boolean(value?.trim());

export const hasRequiredParamValues = (params: AdminCronParam[] | undefined, values: AdminCronParamValues) =>
  (params ?? []).filter((param) => param.required).every((param) => hasParamValue(values[param.key]));

export const hasRequiredParamValuesForVariant = (
  params: AdminCronParam[] | undefined,
  values: AdminCronParamValues,
  variant: AdminCronVariant,
) => {
  const globallyRequiredKeys = (params ?? []).filter((param) => param.required).map((param) => param.key);
  const requiredKeys = [...globallyRequiredKeys];

  for (const key of variant.requiredParamKeys ?? []) {
    if (!requiredKeys.includes(key)) {
      requiredKeys.push(key);
    }
  }

  return requiredKeys.every((key) => hasParamValue(values[key]));
};

export const buildInitialParamValues = (params: AdminCronParam[] | undefined): AdminCronParamValues =>
  (params ?? []).reduce<AdminCronParamValues>((values, param) => {
    // Uses explicit debug defaults when a tool must point at a safe shared mailbox or rollout value.
    // Empty strings remain the normal default so most cron tools keep asking admins for intentional input.
    values[param.key] = param.defaultValue ?? (param.type === "checkbox" ? false : "");
    return values;
  }, {});

export const normalizeAdminCronCargillEmail = (value: string): string | null => {
  const normalized = value.trim().toLowerCase();
  if (!ADMIN_CRON_CARGILL_EMAIL_PATTERN.test(normalized) || normalized.length > 254) {
    return null;
  }

  const localPart = normalized.slice(0, normalized.indexOf("@"));
  if (
    localPart.length === 0 ||
    localPart.length > 64 ||
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..")
  ) {
    return null;
  }

  return normalized;
};
